import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wrench, Loader2 } from "lucide-react";
import SignatureTrackingCard from "./SignatureTrackingCard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SignatureTrackingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signatureRequestId: string;
}

export function SignatureTrackingDialog({ 
  open, 
  onOpenChange, 
  signatureRequestId 
}: SignatureTrackingDialogProps) {
  const [debugMode, setDebugMode] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  
  if (!signatureRequestId) return null;

  const handleManualFinalize = async () => {
    setFinalizing(true);
    toast.info("Manually finalizing signatures...", { description: "Triggering download and archive" });
    try {
      const downloadRes = await supabase.functions.invoke("download-signed-document-internal", {
        body: { signatureRequestId, isPartial: false },
      });
      if ((downloadRes as any).error) throw new Error((downloadRes as any).error.message || "Download failed");

      const archiveRes = await supabase.functions.invoke("archive-governance-document-internal", {
        body: { signatureRequestId },
      });
      if ((archiveRes as any).error) throw new Error((archiveRes as any).error.message || "Archive failed");

      toast.success("Finalization complete", { description: "Signed PDF downloaded and archived" });
    } catch (e: any) {
      toast.error("Manual finalization failed", { description: e?.message || "Unknown error" });
    } finally {
      setFinalizing(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Signature Request Status</DialogTitle>
        </DialogHeader>
        <SignatureTrackingCard 
          signatureRequestId={signatureRequestId} 
          debugMode={debugMode}
        />

        {debugMode && (
          <div className="mt-4 rounded-md border p-4 space-y-3">
            <div className="font-medium">Manual Tools</div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleManualFinalize} disabled={finalizing} variant="secondary" className="gap-2">
                {finalizing && <Loader2 className="h-4 w-4 animate-spin" />}
                Manually Finalize Signatures
              </Button>
            </div>
            <div className="text-xs opacity-70">
              Uses backend functions to force the final download and governance archive for this request.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
